// フィルタ状態と、それを純粋関数として適用するロジック。
// DOM にも URL にも依存しない。state.ts が URLSearchParams との往復を、main.ts が UI 配線を担当する。
import type { HUB_STATIONS, Muni } from "../../schema/muni";
import { METRIC_BETTER, METRIC_GET, type MetricKey, DEFAULT_METRIC } from "./metrics";

export type HubKey = keyof typeof HUB_STATIONS;

export interface FilterState {
  metric: MetricKey;
  pref: string | null;
  floodMax: number | null; // 0..1
  tsunamiMax: number | null; // 0..1
  tsunamiInlandOnly: boolean; // true: 津波 null（内陸）の市区町村のみ
  sedimentMax: number | null; // 0..1
  quakeMax: number | null; // 0..1
  landPriceMax: number | null; // 円/m2
  populationMin: number | null; // 人
  tempJanMin: number | null; // ℃
  tempAugMax: number | null; // ℃
  hub: HubKey;
  hubDistMax: number | null; // km
  excludeUnmeasured: boolean; // true: 効いている条件に値が無い市区町村を一覧から外す
}

export const DEFAULT_FILTER_STATE: FilterState = {
  metric: DEFAULT_METRIC,
  pref: null,
  floodMax: null,
  tsunamiMax: null,
  tsunamiInlandOnly: false,
  sedimentMax: null,
  quakeMax: null,
  landPriceMax: null,
  populationMin: null,
  tempJanMin: null,
  tempAugMax: null,
  hub: "tokyo",
  hubDistMax: null,
  excludeUnmeasured: false,
};

/**
 * 「条件」として数えるフィールド。metric（並び替え）と hub（距離の基準点）は
 * 件数を減らさないので含めない。件数バッジ・0 件時の逃げ道・URL の意味づけが
 * この 1 つの表を見るようにして、どれが条件かの判断を 1 箇所に閉じる。
 */
export const CONDITION_KEYS = [
  "pref",
  "floodMax",
  "tsunamiMax",
  "tsunamiInlandOnly",
  "sedimentMax",
  "quakeMax",
  "landPriceMax",
  "populationMin",
  "tempJanMin",
  "tempAugMax",
  "hubDistMax",
  "excludeUnmeasured",
] as const satisfies readonly (keyof FilterState)[];

export type ConditionKey = (typeof CONDITION_KEYS)[number];

/** 既定値と違う（＝いま効いている）条件のキー。並びは CONDITION_KEYS の順。 */
export function activeConditionKeys(filters: FilterState): ConditionKey[] {
  return CONDITION_KEYS.filter((k) => filters[k] !== DEFAULT_FILTER_STATE[k]);
}

export interface RelaxOption {
  key: ConditionKey;
  /** その条件だけを既定に戻したときの件数。 */
  count: number;
}

/**
 * 0 件になったときの逃げ道。効いている条件を 1 つずつ既定に戻して件数を数え、
 * 効果の大きい順に返す。O(条件数 × 市区町村数)。
 */
export function relaxOptions(munis: Muni[], filters: FilterState): RelaxOption[] {
  return activeConditionKeys(filters)
    .map((key) => ({
      key,
      count: applyFilters(munis, { ...filters, [key]: DEFAULT_FILTER_STATE[key] }).length,
    }))
    .sort((a, b) => b.count - a.count);
}

/** すべての条件を既定に戻す（並び替えの指標と距離の基準点は保つ）。 */
export function clearConditions(filters: FilterState): FilterState {
  const next = { ...filters };
  for (const key of CONDITION_KEYS) {
    Object.assign(next, { [key]: DEFAULT_FILTER_STATE[key] });
  }
  return next;
}

/**
 * 値が欠けうる数値条件の表。どの指標から値を取り、上限・下限のどちらとして効くかを
 * ここ 1 箇所で決める。pref と hubDistMax は値が欠けないので入れない。
 * 津波も入れない。津波 null は「浸水想定の指定が無い内陸」（全国の半分近く）であって
 * 未計測ではないので、上限をいくつにしても該当として扱う。
 */
const NUMERIC_CONDITIONS = {
  floodMax: { get: METRIC_GET.flood, bound: "max" },
  sedimentMax: { get: METRIC_GET.sediment, bound: "max" },
  quakeMax: { get: METRIC_GET.quake, bound: "max" },
  landPriceMax: { get: METRIC_GET.landPrice, bound: "max" },
  populationMin: { get: METRIC_GET.population, bound: "min" },
  tempJanMin: { get: METRIC_GET.tempJan, bound: "min" },
  tempAugMax: { get: METRIC_GET.tempAug, bound: "max" },
} as const satisfies Record<string, { get(muni: Muni): number | null; bound: "max" | "min" }>;

type NumericConditionKey = keyof typeof NUMERIC_CONDITIONS;

const NUMERIC_CONDITION_KEYS = Object.keys(NUMERIC_CONDITIONS) as NumericConditionKey[];

/**
 * 1 つの市区町村が条件に対してどの立場かを表す。
 * "unmeasured" は「落とす理由は無いが、絞り込んだ指標の値を持っていない」状態で、
 * これを "match" と混ぜると「条件と結果が食い違って見える」件数になる。
 */
export type Verdict = "match" | "unmeasured" | "reject";

export function verdictOf(muni: Muni, filters: FilterState): Verdict {
  if (filters.pref !== null && muni.pref !== filters.pref) return "reject";
  const tsunami = muni.hazard.tsunamiRatio;
  if (filters.tsunamiInlandOnly && tsunami !== null) return "reject";
  if (filters.tsunamiMax !== null && tsunami !== null && tsunami > filters.tsunamiMax) return "reject";
  if (filters.hubDistMax !== null && muni.distKm[filters.hub] > filters.hubDistMax) return "reject";

  let unmeasured = false;
  for (const key of NUMERIC_CONDITION_KEYS) {
    const limit = filters[key];
    if (limit === null) continue;

    const { get, bound } = NUMERIC_CONDITIONS[key];
    const value = get(muni);
    if (value === null) {
      unmeasured = true;
      continue;
    }
    if (bound === "max" ? value > limit : value < limit) return "reject";
  }

  return unmeasured ? "unmeasured" : "match";
}

export function applyFilters(munis: Muni[], filters: FilterState): Muni[] {
  return munis.filter((m) => {
    const verdict = verdictOf(m, filters);
    if (verdict === "reject") return false;
    return !(verdict === "unmeasured" && filters.excludeUnmeasured);
  });
}

/**
 * 件数の内訳。excludeUnmeasured は見ない。除外中も「いま何件を除いているか」を
 * 出さないと、トグルを戻す判断ができないため。
 */
export function countVerdicts(munis: Muni[], filters: FilterState): { matched: number; unmeasured: number } {
  let matched = 0;
  let unmeasured = 0;
  for (const muni of munis) {
    const verdict = verdictOf(muni, filters);
    if (verdict === "match") matched++;
    else if (verdict === "unmeasured") unmeasured++;
  }
  return { matched, unmeasured };
}

/**
 * 選択指標の「望ましい順」に並べる（値が無いものは常に末尾）。
 * 既定は昇順で、人口や店舗数のように better: "high" の指標だけ降順になる。
 */
export function sortByMetric(munis: Muni[], metric: MetricKey): Muni[] {
  const get = METRIC_GET[metric];
  const sign = METRIC_BETTER[metric] === "high" ? -1 : 1;
  return [...munis].sort((a, b) => {
    const av = get(a);
    const bv = get(b);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return (av - bv) * sign;
  });
}

export const LIST_LIMIT = 200;

export function filterAndRank(munis: Muni[], filters: FilterState): Muni[] {
  return sortByMetric(applyFilters(munis, filters), filters.metric).slice(0, LIST_LIMIT);
}
